import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { ArrowIcon, VisitMonacoLogo } from '@net0-calc/common/assets/svg';
import { DownloadMethodology } from '@net0-calc/common/components';
import { LanguageDropdown } from 'components';
import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

const SIDEBAR_WIDTH = 200;
const TOP_BAR_HEIGHT = 52;

interface MainLayoutProps {
  children: ReactNode;
  onPrevStep?: () => void;
  isBackButtonVisible: boolean;
  isResultPage: boolean;
  sidebarContent?: ReactNode;
}

export const MainLayout: FC<MainLayoutProps> = ({
  children,
  onPrevStep,
  isBackButtonVisible,
  isResultPage,
  sidebarContent,
}) => {
  const { t, i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage || 'en';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);

  const handleSidebarMouseMove = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const el = sidebarRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    el.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  }, []);

  return (
    <Root>
      <Sidebar ref={sidebarRef} $open={sidebarOpen} onMouseMove={handleSidebarMouseMove}>
        <SidebarGlow />
        <SidebarInner>
          <SidebarCloseButton
            type="button"
            aria-label={t('general.aria.closeMenu')}
            onClick={() => setSidebarOpen(false)}>
            ×
          </SidebarCloseButton>
          <LogoLink href={`https://cvb.visitmonaco.com/${resolvedLanguage}`} target="_blank" rel="noreferrer">
            <Logo />
          </LogoLink>
          <SidebarSiteLink href={`https://cvb.visitmonaco.com/${resolvedLanguage}`} target="_blank" rel="noreferrer">
            {t('general.sidebar.visitMonacoLink')}
          </SidebarSiteLink>
          {sidebarContent && <SidebarNav>{sidebarContent}</SidebarNav>}
          <MonacoFlagBar />
        </SidebarInner>
      </Sidebar>

      <HamburgerButton
        type="button"
        aria-label={t('general.aria.openMenu')}
        $hidden={sidebarOpen}
        onClick={() => setSidebarOpen(true)}>
        <span />
        <span />
        <span />
      </HamburgerButton>
      <SidebarOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} aria-hidden="true" />

      <ContentColumn>
        <TopBar>
          <TopBarLeft>
            <TopBarLabel>{t('general.topBar.title')}</TopBarLabel>
            <TopBarSiteLink href={`https://cvb.visitmonaco.com/${resolvedLanguage}`} target="_blank" rel="noreferrer">
              {t('general.topBar.visitMonacoLink')}
            </TopBarSiteLink>
          </TopBarLeft>
          <TopBarRight>
            {isResultPage && <StyledDownloadMethodology />}
            <LanguageDropdown />
          </TopBarRight>
        </TopBar>

        {(isBackButtonVisible || isResultPage) && (
          <BackRow>
            {isBackButtonVisible ? (
              <CircleBackButton type="button" onClick={onPrevStep}>
                <ArrowIcon />
                <BackButtonText>{t('general.button.backButton')}</BackButtonText>
              </CircleBackButton>
            ) : (
              <div />
            )}
            {isResultPage && <MobileDownloadMethodology />}
          </BackRow>
        )}

        <Main>
          <StyledContainer className="container">{children}</StyledContainer>
        </Main>
      </ContentColumn>
    </Root>
  );
};

/* ─── Layout Shell ─── */

const Root = styled.div`
  width: 100%;
  min-height: 100%;
  position: relative;
  display: flex;
`;

/* ─── Sidebar (Liquid Glass) ─── */

const Sidebar = styled.aside<{ $open: boolean }>`
  --mouse-x: 50%;
  --mouse-y: 50%;

  position: fixed;
  left: 0;
  top: 0;
  width: ${SIDEBAR_WIDTH}px;
  height: 100vh;
  z-index: 600;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
  overflow: hidden;

  background: rgba(20, 20, 22, 0.72);
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  border-right: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: ${1023}px) {
    transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
    width: 280px;
    background: rgba(20, 20, 22, 0.88);
    backdrop-filter: blur(32px) saturate(1.4);
    -webkit-backdrop-filter: blur(32px) saturate(1.4);
  }
`;

const SidebarGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;

  background: radial-gradient(
    320px circle at var(--mouse-x) var(--mouse-y),
    rgba(206, 17, 38, 0.12) 0%,
    rgba(206, 17, 38, 0.04) 30%,
    transparent 70%
  );
  transition: background 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(
      180deg,
      transparent 0%,
      rgba(206, 17, 38, 0.2) 20%,
      rgba(255, 255, 255, 0.06) 50%,
      rgba(206, 17, 38, 0.15) 80%,
      transparent 100%
    );
  }
`;

const SidebarInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  height: 100%;
  position: relative;
  z-index: 1;
`;

const SidebarCloseButton = styled.button`
  display: none;
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  font-size: 28px;
  line-height: 1;
  color: var(--color5);
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  &:focus-visible {
    outline: 2px solid var(--color2);
    outline-offset: 2px;
  }
  @media (max-width: ${1023}px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const LogoLink = styled.a`
  display: block;
  margin-bottom: 20px;
  flex-shrink: 0;
`;

const Logo = styled(VisitMonacoLogo)`
  width: 140px;
  height: auto;
  display: block;
  color: white;
`;

const SidebarSiteLink = styled.a`
  ${TYPOGRAPHY.captionRegular12}
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  margin-bottom: 24px;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  overflow-y: auto;
`;

const MonacoFlagBar = styled.div`
  width: 4px;
  height: 32px;
  background-color: var(--color2);
  margin-top: auto;
  margin-left: 0;
  flex-shrink: 0;
  border-radius: 2px;
  box-shadow: 0 0 12px rgba(206, 17, 38, 0.4);
`;

/* ─── Hamburger (mobile only) ─── */

const HamburgerButton = styled.button<{ $hidden?: boolean }>`
  display: none;
  position: fixed;
  top: 4px;
  left: 4px;
  z-index: 501;
  width: 44px;
  height: 44px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  background: var(--color11);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease, visibility 0.2s ease;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};

  &:hover {
    background-color: #3d3d3d;
  }
  &:focus-visible {
    outline: 2px solid var(--color2);
    outline-offset: 2px;
  }
  span {
    width: 22px;
    height: 2px;
    background: var(--color5);
    border-radius: 1px;
  }

  @media (max-width: ${1023}px) {
    display: flex;
  }
  @media (min-width: 1024px) {
    display: none;
  }
`;

const SidebarOverlay = styled.div<{ $open: boolean }>`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 599;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.3s ease;

  @media (max-width: ${1023}px) {
    display: block;
  }
`;

/* ─── Content Column ─── */

const ContentColumn = styled.div`
  flex: 1;
  margin-left: ${SIDEBAR_WIDTH}px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--color5);

  @media (max-width: ${1023}px) {
    margin-left: 0;
    padding-top: ${TOP_BAR_HEIGHT}px;
  }
`;

/* ─── Top Bar ─── */

const TopBar = styled.div`
  height: ${TOP_BAR_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 0 24px;
  flex-shrink: 0;

  /* Liquid glass effect — warm cream tint */
  background: rgba(245, 230, 208, 0.65);
  backdrop-filter: blur(20px) saturate(1.3);
  -webkit-backdrop-filter: blur(20px) saturate(1.3);
  border-bottom: 1px solid rgba(232, 221, 208, 0.5);
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.5);

  @media (max-width: ${1023}px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 500;
    padding: 0 12px 0 56px;
    height: ${TOP_BAR_HEIGHT}px;
    gap: 8px;
    background: rgba(245, 230, 208, 0.78);
    backdrop-filter: blur(24px) saturate(1.4);
    -webkit-backdrop-filter: blur(24px) saturate(1.4);
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1;
`;

const TopBarLabel = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${1023}px) {
    font-size: 14px;
  }
`;

const TopBarSiteLink = styled.a`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
  text-decoration: none;
  transition: color 0.2s ease;
  display: none;

  &:hover {
    color: var(--color2);
  }

  @media ${DEVICE.laptop} {
    display: inline;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: ${1023}px) {
    gap: 8px;
  }
`;

const StyledDownloadMethodology = styled(DownloadMethodology)`
  display: none;
  @media ${DEVICE.laptop} {
    display: flex;
  }
`;

/* ─── Step Nav Row ─── */

const BackRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 0;

  @media ${DEVICE.laptop} {
    padding: 20px 80px 0;
  }
`;

const MobileDownloadMethodology = styled(DownloadMethodology)`
  @media ${DEVICE.laptop} {
    display: none;
  }
`;

const CircleBackButton = styled.button`
  display: none;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  border-radius: 50%;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  @media ${DEVICE.laptop} {
    display: flex;
  }

  &:hover {
    opacity: 0.85;
  }
  &:focus-visible {
    outline: 2px solid var(--color2);
    outline-offset: 3px;
  }
  & > svg {
    width: 36px;
    height: 36px;
    padding: 8px;
    border: 2px solid var(--color6);
    border-radius: 50%;
    box-sizing: border-box;
    transform: rotate(90deg);
  }
  & path {
    stroke: var(--color6);
  }
`;

const BackButtonText = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  display: none;
  @media ${DEVICE.laptop} {
    display: inline;
  }
`;

/* ─── Main Content ─── */

const Main = styled.main`
  flex: 1;
  padding: 12px 16px 24px;
  display: flex;
  flex-direction: column;

  @media ${DEVICE.laptop} {
    padding: 32px 80px 70px;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
